import React, { useCallback, useState } from 'react';
import {
  Input,
  Button,
  Modal,
  Box,
  SpaceBetween,
  FormField,
} from '@amzn/awsui-components-console';
import { SearchState } from '../SearchPage';
import { MIN_SEARCH_CRITERIA_MESSAGE } from '../constants';
import { saveSearch, generateLabel } from './savedSearchUtils';
import InfoTooltip from 'src/common/ui/InfoTooltip';
import styles from './SearchBar.module.scss';

interface SearchBarProps {
  readonly queryText: string;
  readonly onQueryChange: (query: string) => void;
  readonly onSearch: () => void;
  readonly currentSearchState: SearchState;
  /**
   * Whether the current state satisfies the minimum search criteria. Computed by
   * `SearchPage` (see `hasMinimumSearchCriteria`) and used to gate EVERY submit
   * path in this component — the button and the Enter key.
   */
  readonly canSearch: boolean;
}

/** Maximum character length for a saved search name */
const MAX_NAME_LENGTH = 50;

const SAVE_SEARCH_HELP_TEXT =
  'Save your current search filters as a bookmark for quick access. ' +
  'Up to 5 searches are saved — if you save a 6th, the oldest is auto-deleted.';

const SearchBar: React.FC<SearchBarProps> = ({
  queryText,
  onQueryChange,
  onSearch,
  currentSearchState,
  canSearch,
}) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');

  const handleKeyDown = useCallback(
    (event: CustomEvent<{ key: string }>) => {
      /*
       * Enter is a second submit path and must honour the same gate as the
       * button — a disabled button alone would leave the keyboard route open.
       * Typing is never blocked; only submission is.
       */
      if (event.detail.key === 'Enter' && canSearch) {
        onSearch();
      }
    },
    [onSearch, canSearch],
  );

  const handleSaveSearchClick = useCallback(() => {
    // Pre-populate with auto-generated label as a suggestion
    setSaveSearchName(generateLabel(currentSearchState));
    setShowSaveModal(true);
  }, [currentSearchState]);

  const handleSaveConfirm = useCallback(() => {
    const trimmedName = saveSearchName.trim();
    if (trimmedName.length === 0) return;
    saveSearch(currentSearchState, trimmedName);
    setShowSaveModal(false);
    setSaveSearchName('');
  }, [saveSearchName, currentSearchState]);

  const handleSaveCancel = useCallback(() => {
    setShowSaveModal(false);
    setSaveSearchName('');
  }, []);

  const canSave =
    queryText.trim().length > 0 ||
    Object.values(currentSearchState.filters).some((v) => v && v.length > 0);

  const isSaveDisabled = saveSearchName.trim().length === 0;

  return (
    <>
      {/*
        The reason lives on a FormField wrapping the whole row rather than on the
        input alone, because the criterion can be satisfied EITHER by the query
        input OR by any filter dropdown — it is a form-level rule, not a
        property of one field.

        `constraintText` (neutral) rather than `errorText` (red + alert icon,
        which is how the Translated Locale field reports its blocked state):
        an unmet minimum is the pristine state of the page on first load, so an
        error treatment would flag a mistake the user has not made yet. It still
        satisfies the same requirement as that field — the blocking reason is
        readable without hovering, no tooltip involved — and it disappears as
        soon as a criterion is supplied.

        Deliberately NOT Button `disabledReason`. That is a perfectly valid
        Cloudscape pattern — it swaps native `disabled` for `aria-disabled` on
        purpose, so the button stays focusable and its tooltip reachable, and the
        hidden span is the a11y association, not duplication. It is simply the
        weaker fit here: `disabledReason` describes the BUTTON, whereas
        `constraintText` is wired to the Input via FormField's
        `aria-describedby`. Since a natively disabled button leaves the tab
        order, keyboard users land on the query input — the field they need to
        act on — and hear the reason there, with no hover or tooltip involved.
      */}
      <FormField
        constraintText={canSearch ? undefined : MIN_SEARCH_CRITERIA_MESSAGE}
      >
        <div className={styles.container}>
          <div className={styles.inputWrapper}>
            <Input
              value={queryText}
              onChange={({ detail }) => onQueryChange(detail.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search by String ID, source text or translated text..."
              type="search"
              ariaLabel="Search query"
            />
          </div>
          <Button variant="primary" onClick={onSearch} disabled={!canSearch}>
            Search
          </Button>
          <Button
            variant="normal"
            onClick={handleSaveSearchClick}
            disabled={!canSave}
            iconName="star"
          >
            Save search
          </Button>
          <InfoTooltip content={SAVE_SEARCH_HELP_TEXT} />
        </div>
      </FormField>

      <Modal
        visible={showSaveModal}
        onDismiss={handleSaveCancel}
        header="Save search"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleSaveCancel}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveConfirm}
                disabled={isSaveDisabled}
              >
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <FormField
          label="Search name"
          constraintText={`${saveSearchName.length}/${MAX_NAME_LENGTH} characters`}
        >
          <Input
            value={saveSearchName}
            onChange={({ detail }) =>
              setSaveSearchName(detail.value.slice(0, MAX_NAME_LENGTH))
            }
            placeholder="Enter a name for this saved search"
            ariaLabel="Saved search name"
          />
        </FormField>
      </Modal>
    </>
  );
};

export default SearchBar;
